import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "stores/RootStore";
import ServiceStore from "stores/ServiceStore";
// Using API types for ServiceStore entities
import type { Account } from "types/common/ServiceStoreTypes";

export class AccountsListStore {
  rootStore: RootStore;

  // Observables
  loading = false;
  error: string | null = null;
  filtersVisible = true; // Show filters by default

  // Constructor
  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // Actions
  toggleFiltersVisibility() {
    this.filtersVisible = !this.filtersVisible;
  }

  // Async actions
  async loadAccounts() {
    runInAction(() => {
      this.loading = true;
      this.error = null;
    });

    try {
      // Load all relational data to ensure complete information is available
      await Promise.all([
        // Load all base entities without queries to populate stores
        this.accountsStore.find(),
        this.contactsStore.find(),
        this.opportunitiesStore.find(),
        this.jobStore.find(),
        this.activitiesStore.find(),
      ]);
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to load accounts";
      });
      console.error("Failed to load accounts:", error);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async reloadAccounts() {
    await this.loadAccounts();
  }

  async createAccount(accountData: Partial<Account>) {
    this.loading = true;
    this.error = null;

    try {
      const account = await this.accountsStore.create(accountData);

      // Auto-create welcome activity
      try {
        await this.activitiesStore.create({
          ActivityType: "Task",
          Subject: `Welcome new account: ${accountData.name}`,
          accountId: (account as Record<string, unknown>).accountId,
          Priority: "Medium",
          Status: "Scheduled",
          AssignedTo: this.rootStore.authStore?.currentUser?.id,
          ScheduledDate: new Date(
            Date.now() + 24 * 60 * 60 * 1000
          ).toISOString(), // Tomorrow
        });
      } catch (activityError) {
        console.warn("Failed to create welcome activity:", activityError);
      }

      // Reload accounts to include the new one
      await this.loadAccounts();

      return account;
    } catch (error) {
      this.error =
        error instanceof Error ? error.message : "Failed to create account";
      console.error("Failed to create account:", error);
      throw error;
    } finally {
      this.loading = false;
    }
  }

  async updateAccount(accountId: number, accountData: Partial<Account>) {
    try {
      const updatedAccount = await this.accountsStore.patch(
        accountId,
        accountData
      );

      // The service store will automatically update its entities
      return updatedAccount;
    } catch (error) {
      this.error =
        error instanceof Error ? error.message : "Failed to update account";
      console.error("Failed to update account:", error);
      throw error;
    }
  }

  async deleteAccount(accountId: number) {
    try {
      await this.accountsStore.remove(accountId);

      // The service store will automatically update its entities
    } catch (error) {
      this.error =
        error instanceof Error ? error.message : "Failed to delete account";
      console.error("Failed to delete account:", error);
      throw error;
    }
  }

  async createOpportunityForAccount(
    accountId: number,
    opportunityData: Record<string, unknown>
  ) {
    try {
      const opportunity = await this.opportunitiesStore.create({
        ...opportunityData,
        accountId: accountId,
      });

      // Create follow-up activity
      await this.activitiesStore.create({
        ActivityType: "Follow-up",
        Subject: `Follow up on new opportunity: ${opportunityData.opportunityName}`,
        accountId: accountId,
        opportunityId: (opportunity as Record<string, unknown>).opportunityId,
        AssignedTo: this.rootStore.authStore?.currentUser?.id,
        ScheduledDate: new Date(
          Date.now() + 3 * 24 * 60 * 60 * 1000
        ).toISOString(), // 3 days
      });

      return opportunity;
    } catch (error) {
      console.error("Failed to create opportunity for account:", error);
      throw error;
    }
  }

  async addContactToAccount(
    accountId: number,
    contactData: Record<string, unknown>
  ) {
    try {
      return await this.contactsStore.create({
        ...contactData,
        accountId: accountId,
      });
    } catch (error) {
      console.error("Failed to add contact to account:", error);
      throw error;
    }
  }

  // Computed values
  get accountsStore(): ServiceStore {
    return this.rootStore.accountsStore as ServiceStore;
  }

  get activitiesStore(): ServiceStore {
    return this.rootStore.activitiesStore as ServiceStore;
  }

  get opportunitiesStore(): ServiceStore {
    return this.rootStore.opportunitiesStore as ServiceStore;
  }

  get contactsStore(): ServiceStore {
    return this.rootStore.contactsStore as ServiceStore;
  }

  get jobStore(): ServiceStore {
    return this.rootStore.jobStore as ServiceStore;
  }

  get accounts(): Account[] {
    return this.accountsStore.entities as unknown[] as Account[];
  }

  get hasFilters(): boolean {
    return false; // Filters are now handled by the datagrid
  }
}

export default AccountsListStore;
